import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { BreadcrumbGroup } from "@awsui/components-react";
import theme from "../../common/theme";
import { Icon, Select, TextContent } from "@awsui/components-react";
import * as Humanize from "humanize-plus";
import {
  listCampaignLabelingJobs,
  listCampaignPrefilteringJobs,
} from "../../common/api";
import {
  CampaignLabelingJob,
  CampaignPrefilteringJob,
} from "../../appstore/models";

export interface BreadbrumbItem {
  text: string;
  href: string;
}

export interface BreadcrumbsProps {
  items: BreadbrumbItem[];
}

export const Breadcrumbs = (props: BreadcrumbsProps) => {
  return <BreadcrumbGroup items={props.items} ariaLabel="Breadcrumbs" />;
};

export const ContentContainer = styled.section`
  padding: 1rem 2rem;
  padding-top: 3rem;
`;

export const ContentSection = styled.section`
  padding: 2rem 0;
`;

const StatusTextContainer = styled.div<{ color: string }>`
  color: ${(p) => p.color};
  font-weight: bold;
  span:first-child {
    margin-right: 5px;
  }
`;

export const StatusText = (props: {
  status: "success" | "warning" | "error" | "in_progress" | "running";
  children: React.ReactNode;
}) => {
  const { status, children } = props;
  let color = theme.green600;
  let iconName:
    | "status-positive"
    | "status-warning"
    | "status-negative"
    | "status-in-progress"
    | "status-pending" = "status-positive";
  if (status === "warning") {
    color = theme.orange500;
    iconName = "status-warning";
  } else if (status === "error") {
    color = theme.red600;
    iconName = "status-negative";
  } else if (status === "in_progress") {
    color = theme.grey600;
    iconName = "status-in-progress";
  } else if (status === "running") {
    color = theme.blue600;
    iconName = "status-pending";
  }
  return (
    <StatusTextContainer color={color}>
      <Icon name={iconName} size="normal" variant="normal" />
      <>{children}</>
    </StatusTextContainer>
  );
};

export const StyledTextContent = styled(TextContent)`
  margin-bottom: 1rem;
`;

export const LargeTextValue = styled.div<{
  color?: string;
}>`
  font-size: 4.4rem;
  color: ${(p) => (p.color ? p.color : "#333")};
  margin: 1rem 0rem 2rem;
  font-weight: 100;
`;

export const CompactInt = (props: { className?: string; value: number }) => {
  const { value, className } = props;
  return (
    <span className={className} title={Humanize.intComma(value)}>
      {Humanize.compactInteger(value)}
    </span>
  );
};

export const ErrorText = styled.div`
  color: ${theme.red600};
  font-weight: bold;
  font-size: 1.1rem;
  span:first-child {
    margin-right: 5px;
  }
`;

export const ErrorMessage = (props: { message: string }) => {
  return (
    <ErrorText>
      <Icon name="status-warning" size="normal" variant="normal" />
      {props.message}
    </ErrorText>
  );
};

export const SuccessMessage = styled.div`
  color: ${theme.green600};
  font-weight: bold;
  font-size: 1.1rem;
`;

export const FooterContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
`;

export const FooterMessage = styled.div`
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

export const FooterButtons = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const StyledSelect = styled(Select)`
  width: 100%;
`;

/**
 * Select input component listing campaign prefiltering jobs for the specified
 * campaign job name
 */
export const SelectCampaignPrefilteringJob = (props: {
  campaignJobName: string;
  selectedCampaignPrefilteringJob: CampaignPrefilteringJob | undefined;
  onChange: (
    campaignPrefilteringJob: CampaignPrefilteringJob | undefined
  ) => void;
}) => {
  const { campaignJobName, selectedCampaignPrefilteringJob, onChange } = props;
  const [campaignPrefilteringJobs, setCampaignPrefilteringJobs] = useState<
    CampaignPrefilteringJob[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const loadJobs = useCallback(async () => {
    setIsLoading(true);
    const listCampaignPrefilteringJobsResult =
      await listCampaignPrefilteringJobs(campaignJobName, 100);
    setCampaignPrefilteringJobs(
      listCampaignPrefilteringJobsResult.campaignPrefilteringJobs
    );
    setIsLoading(false);
  }, [campaignJobName]);
  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  return (
    <StyledSelect
      selectedOption={
        selectedCampaignPrefilteringJob
          ? {
              label: selectedCampaignPrefilteringJob.CampaignPrefilteringId,
              value: selectedCampaignPrefilteringJob.CampaignPrefilteringId,
            }
          : null
      }
      onChange={(event) => {
        const prefilteringJobId = event.detail.selectedOption.value;
        const prefilteringJob = campaignPrefilteringJobs.find(
          (o) => o.CampaignPrefilteringId === prefilteringJobId
        );
        onChange(prefilteringJob);
      }}
      options={campaignPrefilteringJobs.map((o) => ({
        label: o.CampaignPrefilteringId,
        value: o.CampaignPrefilteringId,
        disabled: o.Status !== "Complete",
      }))}
      loadingText="Loading jobs"
      placeholder={
        campaignPrefilteringJobs.length === 0 && !isLoading
          ? "No prefiltering jobs found"
          : "Choose a prefiltering job"
      }
      selectedAriaLabel="Selected"
      statusType={isLoading ? "loading" : "finished"}
      virtualScroll
    />
  );
};

/**
 * Select input component listing campaign prefiltering jobs for the specified
 * campaign job name
 */
export const SelectCampaignLabelingJob = (props: {
  campaignJobName: string;
  selectedCampaignLabelingJob: CampaignLabelingJob | undefined;
  onChange: (campaignLabelingJob: CampaignLabelingJob | undefined) => void;
}) => {
  const { campaignJobName, selectedCampaignLabelingJob, onChange } = props;
  const [campaignLabelingJobs, setCampaignLabelingJobs] = useState<
    CampaignLabelingJob[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const loadJobs = useCallback(async () => {
    setIsLoading(true);
    const listCampaignLabelingJobsResult = await listCampaignLabelingJobs(
      campaignJobName,
      100
    );
    setCampaignLabelingJobs(
      listCampaignLabelingJobsResult.campaignLabelingJobs
    );
    setIsLoading(false);
  }, [campaignJobName]);
  useEffect(() => {
    loadJobs();
  }, [loadJobs]);
  return (
    <StyledSelect
      selectedOption={
        selectedCampaignLabelingJob
          ? {
              label: selectedCampaignLabelingJob.GroundTruthLabelingJobName,
              value: selectedCampaignLabelingJob.GroundTruthLabelingJobName,
            }
          : null
      }
      onChange={(event) => {
        const labelingJobName = event.detail.selectedOption.value;
        const prefilteringJob = campaignLabelingJobs.find(
          (o) => o.GroundTruthLabelingJobName === labelingJobName
        );
        onChange(prefilteringJob);
      }}
      options={campaignLabelingJobs.map((o) => ({
        label: o.GroundTruthLabelingJobName,
        value: o.GroundTruthLabelingJobName,
      }))}
      loadingText="Loading jobs"
      placeholder={
        campaignLabelingJobs.length === 0 && !isLoading
          ? "No labeling jobs found"
          : "Choose a labeling job"
      }
      selectedAriaLabel="Selected"
      statusType={isLoading ? "loading" : "finished"}
      virtualScroll
    />
  );
};

export const asyncPaginateSelectStyles = {
  option: (
    provided: any,
    {
      isDisabled,
      isFocused,
      isSelected,
    }: {
      isDisabled: boolean;
      isFocused: boolean;
      isSelected: boolean;
    }
  ) => ({
    ...provided,
    backgroundColor: isSelected
      ? theme.blue100
      : isFocused
      ? theme.grey200
      : "rgba(0,0,0,0)",
    color: theme.textBodyDefault,
    cursor: isDisabled ? "not-allowed" : "default",
    ":active": {
      ...provided[":active"],
      backgroundColor: !isDisabled ? theme.blue100 : undefined,
    },
    border: isSelected ? `2px solid ${theme.blue300}` : undefined,
  }),
  menuList: (provided: any, state: any) => ({
    ...provided,
    margin: 0,
    padding: 0,
  }),
  menu: (provided: any, state: any) => ({
    ...provided,
    borderRadius: 0,
    margin: 0,
    border: "1px solid rgba(0, 26, 36 , .3)",
    boxShadow:
      "0 1px 1px 0 var(--color-shadow-medium-3CtI4L, rgba(0, 28, 36, 0.3)), 1px 1px 1px 0 var(--color-shadow-side-2cOaCx, rgba(0, 28, 36, 0.15)), -1px 1px 1px 0 var(--color-shadow-side-2cOaCx, rgba(0, 28, 36, 0.15))",
  }),
  control: (provided: any, state: any) => ({
    ...provided,
    borderRadius: "2px",
    lineHeight: "2.2rem",
    minHeight: "3.2rem",
    maxHeiht: "3.2rem",
    border: "1px solid var(--color-border-input-default-27WVST, #aab7b8)",
  }),
};
